import { Icon } from "@mdi/react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { mdiCancel } from "@mdi/js";
import { Button, Layout, Progress, Typography } from "antd";
import { IS_PROD_ENV } from "constants/app.constants";
import styled from "styled-components";

import { useUser } from "hooks";
import useBetaFeatures from "hooks/useBetaFeatures";

import { IconButton } from "components/forecasting/Shared";

import AutomationCommandsList from "./components/AutomationCommandsList";
import AutomationListPanel from "./components/AutomationListPanel";
import { useAutomationBroker } from "./contexts/AutomationBrokerContext";
import useAutomationExecution from "./hooks/useAutomationExecution";
import useFetchAutomationCollections from "./hooks/useFetchAutomationCollections";
import {
  AutomationCollection,
  AutomationCommand,
  AutomationGroup
} from "./types/automation";

const RootContainer = styled(Layout)`
  display: grid;
  grid-template-rows: minmax(0, 1fr);
  height: 100%;
  padding: 10px;
`;

const ButtonContainer = styled.div`
  position: absolute;
  justify-content: flex-end;
  right: 20px;
  top: 20px;
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StatusContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 10px;
  min-width: 200px;
`;

const CommandRenderContainer = styled.div`
  height: 100%;
  max-height: 100%;
`;

const ContentContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, auto) minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  height: 100%;
`;

const JobStatusContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const AutomationWidget = () => {
  const { data: automationCollections } = useFetchAutomationCollections();
  const { email } = useUser();
  const { hasFeature } = useBetaFeatures();
  const [automations, setAutomations] =
    useState<AutomationCollection[]>(automationCollections);

  const [selectedAutomation, setSelectedAutomation] =
    useState<AutomationCollection | null>(null);

  const [selectedCommand, setSelectedCommand] = useState<AutomationCommand | null>(null);

  const {
    isProcessing,
    currentJobId,
    jobStatus,
    progress,
    executeAutomation,
    cancelJob,
    statusText
  } = useAutomationExecution();

  const broker = useAutomationBroker();

  const isJobActive =
    currentJobId &&
    jobStatus &&
    (jobStatus.status === "waiting" || jobStatus.status === "active");

  useEffect(() => {
    setAutomations(automationCollections);
    if (!selectedAutomation) {
      setSelectedAutomation(
        automationCollections?.length ? automationCollections[0] : null
      );
      return;
    }

    const existing = automationCollections.find(
      (coll) => coll._id === selectedAutomation?._id
    );
    if (existing) {
      setSelectedAutomation(existing);
    }
  }, [automationCollections]);

  const handleClick = async () => {
    if (!selectedAutomation) {
      toast.error("No automation selected. Please select an automation to execute.");
      return;
    }

    try {
      await executeAutomation(selectedAutomation._id, selectedAutomation.name);
      toast.success("Automation job started successfully!");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      toast.error(`Failed to start automation: ${errorMessage}`);
    }
  };

  const handleGroupsChange = useCallback(
    (groups: AutomationGroup[]) => {
      const idx = automations.findIndex((a) => a === selectedAutomation);
      if (idx < 0) {
        return;
      }
      const newAutomation = { ...selectedAutomation, groups };
      automations.splice(idx, 1, newAutomation);
      setAutomations([...automations]);
      setSelectedAutomation(newAutomation);
    },
    [automations, selectedAutomation]
  );
  // Handle job status changes for toast notifications
  useEffect(() => {
    if (jobStatus) {
      if (jobStatus.status === "completed") {
        toast.success("Automation completed successfully!");
      } else if (jobStatus.status === "failed") {
        toast.error(`Automation failed: ${jobStatus.result?.error || "Unknown error"}`);
      }
    }
  }, [jobStatus]);

  useEffect(() => {
    // Only expose broker when ALL these conditions are true:
    // 1. broker exists
    // 2. AUTOMATION flag is set
    // 3. email MATCHES the automation user
    // 4. NOT in production
    const shouldExpose =
      !!broker &&
      !!window.AUTOMATION &&
      email === process.env.REACT_APP_AUTOMATION_USER &&
      !IS_PROD_ENV;

    if (shouldExpose) {
      window.broker = broker;
    } else if (window.broker === broker) {
      // Conditions became false — remove the stale reference we previously set
      delete window.broker;
    }

    return () => {
      // Cleanup on unmount or when any dependency changes
      if (window.broker === broker) {
        delete window.broker;
      }
    };
  }, [broker, email, IS_PROD_ENV]);

  if (!hasFeature("Automation")) {
    return null;
  }

  return (
    <RootContainer data-testid="widget-automation">
      <ContentContainer>
        <AutomationListPanel
          onAutomationChange={setAutomations}
          onSelectedAutomation={setSelectedAutomation}
          automations={automations}
          selectedAutomation={selectedAutomation}
        />

        <CommandRenderContainer>
          <AutomationCommandsList
            onGroupsChange={handleGroupsChange}
            selectedAutomation={selectedAutomation}
            onSelectedCommand={setSelectedCommand}
            selectedCommand={selectedCommand}
          />
        </CommandRenderContainer>
      </ContentContainer>
      <ButtonContainer>
        {isJobActive && (
          <StatusContainer>
            <JobStatusContainer>
              <Typography.Text>{statusText}</Typography.Text>

              <Progress
                percent={progress}
                size="small"
                showInfo={false}
                style={{ width: 150 }}
              />
            </JobStatusContainer>
            <div style={{ display: "flex", gap: "8px" }}>
              <IconButton
                title="Cancel"
                onClick={cancelJob}
                disabled={isProcessing}
                icon={<Icon path={mdiCancel} />}
              />
            </div>
          </StatusContainer>
        )}
        {!isJobActive && (
          <Button
            loading={isProcessing}
            onClick={handleClick}
            type="primary"
            disabled={isJobActive}>
            Execute
          </Button>
        )}
      </ButtonContainer>
    </RootContainer>
  );
};
